import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import Popover2 from "./popover-2";
import Layout from "../layout/layout";
import Bar from "../layout/bar";
import FilterPopoverDropdown from "./filter-popover-dropdown";
import FilterPopoverRadio from "./filter-popover-radio";
import Button from "../elements/button";
import ButtonTooltip from "./button-tooltip";
import Checkbox from "../forms/checkbox";
import Icon from "../elements/icon";

const FilterPopover = ({
  className,
  buttonClassName,
  config,
  activeFilters,
  onFilterChange,
  buttonText = "More Filters",
  buttonType = "toggle",
  showCount = true,
  onApply = _.noop,
  onClear,
  onOpen = _.noop,
  onClose = _.noop,
  placement,
}) => {
  const onTempCheckboxFilterChange = (isChecked, filterKey, section) => {
    const oldSection = activeFilters[section] || [];

    let newSection = isChecked ? oldSection.concat(filterKey) : _.without(oldSection, filterKey);

    const newFilters = { ...activeFilters, [section]: newSection };

    onFilterChange(newFilters);
  };

  const onTempDropdownFilterChange = (section, options) => {
    const newFilters = { ...activeFilters, [section]: options };
    onFilterChange(newFilters);
  };

  const onTempRadioFilterChange = (section, value) => {
    const newFilters = { ...activeFilters, [section]: value };
    onFilterChange(newFilters);
  };

  // Used for calculating the total number of applied filters
  // multi-select options come in as an array but single select (radio)
  // Comes in as a value, in those cases, just increment the total by one
  const numFilters = _.reduce(
    activeFilters,
    (memo, item) => {
      let total = 0;
      if (Array.isArray(item)) {
        total = item.length;
      } else {
        total = 1;
      }
      return memo + total;
    },
    0
  );

  return (
    <Popover2
      className={className}
      placement={placement}
      onClose={() => {
        onApply();
        onClose();
      }}
      trigger={({ togglePopover, is_open }) => (
        <Button
          type={buttonType}
          className={buttonClassName}
          onClick={() => {
            // if not open yet, then it will be opened
            if (!is_open) {
              onOpen();
            }
            togglePopover();
          }}
        >
          {_.isFunction(buttonText) ? (
            buttonText(numFilters, is_open)
          ) : (
            <span>
              {buttonText}
              {numFilters && showCount ? ` (${numFilters})` : ""}
            </span>
          )}
        </Button>
      )}
    >
      {({ closePopover }) => (
        <Layout
          footerBorder={false}
          footer={
            <Bar
              padding="20px 30px"
              right={
                <div>
                  {_.isFunction(onClear) ? (
                    <Button
                      type="simple"
                      onClick={() => {
                        closePopover();
                        onClear();
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                  <Button
                    className="et--filter-apply"
                    onClick={() => {
                      closePopover();
                      onApply();
                    }}
                  >
                    Apply
                  </Button>
                </div>
              }
            />
          }
        >
          {_.map(config, (values, section) => (
            <div className="et--filter-section" key={values.title}>
              {/* title */}
              <div className="et--filter-group-name">
                <div className="et--filter-group-name-title">
                  <strong>{values.title}</strong>
                  <span className="et--filter-group-help">
                    {values.help_tip ? (
                      <ButtonTooltip trigger={<Icon icon="help" style={{ color: "#aec6cc" }} />}>
                        {values.help_tip}
                      </ButtonTooltip>
                    ) : null}
                  </span>
                </div>
              </div>

              <div className="et--filter-group-options">
                {/* checkboxes */}
                {(!values.type || values.type === "checkbox") &&
                  _.map(values.options, ({ name, key, icon, color }) => {
                    const sectionKeys = activeFilters[section] || [];
                    return (
                      <div className="et--filter-option" key={key}>
                        {name !== "spacer" ? (
                          <Checkbox
                            checked={_.contains(sectionKeys, key)}
                            onChange={isChecked => onTempCheckboxFilterChange(isChecked, key, section)}
                            label={name}
                          >
                            {name}
                            {icon ? <Icon icon={icon} className="et--filter-option-icon" style={{ color }} /> : null}
                          </Checkbox>
                        ) : null}
                      </div>
                    );
                  })}

                {/* dropdowns */}
                {values.type === "dropdown" && (
                  <FilterPopoverDropdown
                    filterKey={section}
                    options={values.options}
                    values={activeFilters[section]}
                    onChange={onTempDropdownFilterChange}
                    onSearch={values.onSearch}
                    multiple={values.multiple}
                    searchable={values.searchable}
                    allowChangeAll={values.allowChangeAll}
                    allowUnselectAll={values.allowUnselectAll}
                    width={values.width}
                  />
                )}
                {values.type === "radio" && (
                  <FilterPopoverRadio
                    filterKey={section}
                    options={values.options}
                    onChange={onTempRadioFilterChange}
                    value={activeFilters[section] || values.default}
                  />
                )}
              </div>
            </div>
          ))}
        </Layout>
      )}
    </Popover2>
  );
};

FilterPopover.propTypes = {
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  placement: PropTypes.string,
  config: PropTypes.object,
  activeFilters: PropTypes.object,
  onFilterChange: PropTypes.func,
  buttonText: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  buttonType: PropTypes.string,
  showCount: PropTypes.bool,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default FilterPopover;
